import {
	Component, Input, OnDestroy, OnChanges, AfterViewInit, ViewChild, SimpleChanges, OnInit, ChangeDetectorRef
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MessageService } from 'base';
import { DiscResultsService } from 'discovery';
import { Device } from 'DiscoveryEngine.TS/Core';
import { List, ValueEncoder } from 'DiscoveryEngine.TS/Utils';
import { IdentityService, UserPermissionsService } from 'identity';
import {
	DisplayDeviceListEntryDto, GetDeviceResultDto, IGetPortStateResultDto, ISetPortStateResultDto, PortStateDto,
	IPortStateDto_Get
} from 'UvExplorerServer.Dtos.TS/Session';
import { IfEntry, IfTable } from 'DiscoveryEngine.TS/InfoSets/IfTable';
import { InterfaceSelectorComponent } from '../interface-selector/interface-selector.component';
import { SnmpConstants } from 'DiscoveryEngine.TS/Core/SnmpConstants';
import { PortStateType } from 'UvExplorerServer.Dtos.TS/Session';
import { NavigationService } from 'netview';
import { ToolsService } from 'tools';

@Component({
	selector: 'port-poe-status-editor',
	templateUrl: './port-poe-status-editor.component.html',
	styleUrls: ['./port-poe-status-editor.component.scss']
})
export class PortPoeStatusEditorComponent implements OnChanges, OnDestroy, OnInit {

	@Input() public deviceGuid: string;

	@Input()
	public ifTableHeightVh: number;

	@Input()
	public ifTableHeightPx: number;

	@Input()
	public ifTableMinHeight: number;

	@Input()
	public visible: boolean;

	@ViewChild(InterfaceSelectorComponent, { static: true })
	public ifSelector: InterfaceSelectorComponent;

	public device: Device;
	public deviceName: string;
	public displayDeviceSelector: boolean;

	public form: UntypedFormGroup;
	public poeStatus: UntypedFormControl;

	public selectedInterfaces: IfEntry[];

	constructor(private formBuilder: UntypedFormBuilder,
		private navService: NavigationService,
		private messageService: MessageService,
		private toolsService: ToolsService,
		private discResultsService: DiscResultsService,
		private identityService: IdentityService,
		private userPermissionsService: UserPermissionsService,
		private changeDetector: ChangeDetectorRef
	) {
		this.setDevice(null);
		this.displayDeviceSelector = false;
		this.form = null;
		this.poeStatus = null;
		this.clearSelectedInterfaces();
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (!!changes["deviceGuid"] || !!changes["visible"]) {
			if (!!this.deviceGuid && this.visible) {
				if (this.poeStatus) {
					this.poeStatus.setValue("");
				}
				setTimeout(() => this.getDevice(), 0);
			}
			else {
				if (this.poeStatus) {
					this.poeStatus.setValue("");
				}
				this.setDevice(null);
			}
		}
	}

	public ngOnInit(): void {
		this.rebuildForm();
	}

	public ngOnDestroy(): void {

	}

	private clearSelectedInterfaces(): void {
		this.selectedInterfaces = [];
	}

	private rebuildForm(): void {
		let poeStatus = "";

		this.poeStatus = new UntypedFormControl(poeStatus);

		this.form = this.formBuilder.group({
			poeStatus: poeStatus,
		});
	}

	public get enableSelectDevice(): boolean {
		return true;
	}

	public get enableUpdate(): boolean {
		return (this.userPermissionsService.canManagePortPoe &&
			!!this.device && !!this.poeStatus.value && !!this.selectedInterfaces &&
			(this.selectedInterfaces.length > 0));
	}

	public get enableRefresh(): boolean {
		return !!this.device;
	}

	public selectDevice(): void {
		this.displayDeviceSelector = true;
	}

	public update(): void {
		if (!!this.device && !!this.poeStatus.value && !!this.selectedInterfaces &&
			(this.selectedInterfaces.length > 0)) {

			let sessionGuid = this.identityService.sessionGuid;
			let netViewGuid = this.navService.location.netViewGuid;
			let agentGuid = this.navService.location.agentGuid;
			let schedDiscGuid = this.navService.location.schedDiscGuid;
			let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

			let portStateDtos: PortStateDto[] =
				this.selectedInterfaces.map((ifEntry) => this.createPortStateDto(this.device, ifEntry));

			this.toolsService.setPortState(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
				schedDiscRsltGuid, PortStateType.PoeState, portStateDtos, true).subscribe(
					(result: ISetPortStateResultDto) => {
						let succeeded = false;
						if (result.entries) {
							succeeded = result.entries.every((entry) => entry.succeeded);
						}
						if (succeeded) {
							this.messageService.displayInfoMessage("Ports were successfully configured");
						}
						else {
							this.messageService.displayErrorMessage("Some or all ports could not be configured");
						}
						this.getDevicePortState();
						//this.getDevice();
					},
					(error) => {
						this.messageService.displayErrorMessage("Some or all ports could not be configured");
						this.getDevicePortState();
						//this.getDevice();
					});
		}
	}

	private createPortStateDto(device: Device, ifEntry: IfEntry): PortStateDto {
		let result = new PortStateDto();

		result.DeviceGuid = ValueEncoder.EncodeGuid(device.GUID);
		result.IfIndex = ifEntry.IfIndex;
		result.PoeAdminEnable = this.createPoeState(this.poeStatus.value);

		return result;
	}

	private createPoeState(value: string): number {
		switch (value.toLowerCase()) {
			case "enable":
				return SnmpConstants.pethPsePortAdminEnable_true;
			case "disable":
				return SnmpConstants.pethPsePortAdminEnable_false;
			default:
				return 0;
		}
	}

	public refresh(): void {
		this.getDevicePortState();
	}

	private setDevice(device: Device): void {
		if (device) {
			this.device = device;
			this.deviceName = device.DisplayName;
		}
		else {
			this.device = null;
			this.deviceName = "No device is selected";
		}
	}

	public onDeviceSelectorOk(devices: DisplayDeviceListEntryDto[]): void {
		this.deviceGuid = devices[0].deviceGuid;
		this.getDevice();
	}

	public onDeviceSelectorCancel(): void {
		this.displayDeviceSelector = false;
	}

	private getDevice(): void {

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		let deviceGuids = new List<string>();
		deviceGuids.Add(this.deviceGuid);

		this.messageService.startWork();
		this.discResultsService.getDevice(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, "network", deviceGuids, true).subscribe(
				(result: GetDeviceResultDto) => {
					this.messageService.endWork();
					if (result.HasDevices) {
						this.setDevice(result.Devices.Get(0));
						//this.getDevicePortState();
						this.changeDetector.detectChanges();
					}
					else {
						this.messageService.displayErrorMessage("Could not retrieve selected device");
					}
				},
				(error?) => {
					this.messageService.endWork();
					this.messageService.displayErrorMessage("Could not retrieve selected device");
				});
	}

	private getDevicePortState(): void {

		if (!!this.device && this.device.HasIfTable) {
			let sessionGuid = this.identityService.sessionGuid;
			let netViewGuid = this.navService.location.netViewGuid;
			let agentGuid = this.navService.location.agentGuid;
			let schedDiscGuid = this.navService.location.schedDiscGuid;
			let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

			this.messageService.startWork();
			this.toolsService.getPortState(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
				schedDiscRsltGuid, [PortStateType.AdminStatus, PortStateType.PoeState, PortStateType.ConnectedDevices],
				[this.deviceGuid], [], true).subscribe(
					(result: IGetPortStateResultDto) => {
						this.messageService.endWork();
						if (!!this.device && this.device.HasIfTable) {
							let errors = false;
							let message = "";

							if (!result.entries || result.entries.length == 0) {
								errors = true;
							}
							else {
								errors = result.entries.some((entry: IPortStateDto_Get) => {
									if (!entry.succeeded) {
										message = entry.message;
										return true;
									}
									else {
										return false;
									}
								});
							}

							if (errors) {
								this.messageService.displayErrorMessage("Could not retrieve device port state");
								console.error(message);
							}
							else {
								let ifTable: IfTable = this.device.IfTable;

								result.entries.forEach((entry: IPortStateDto_Get) => {
									let ifEntry: IfEntry = ifTable.Find(entry.ifIndex);
									if (ifEntry) {
										ifEntry.PoeAdminEnable = entry.poeAdminEnable;
										ifEntry.PoeDetectionStatus = entry.poeDetectionStatus;
										ifEntry.ConnectedDevices = entry.connectedDevices;
									}
									else {
										console.warn(`Missing device interface: ${this.device.DisplayName} - ${entry.ifIndex}`);
									}
								});

								this.ifSelector.rebuildEntries();
								this.changeDetector.detectChanges();
							}
						}
					},
					(error?) => {
						this.messageService.endWork();
						this.messageService.displayErrorMessage("Could not retrieve device port state");
					});
		}
	}

}
